import { toEngagementIdentity } from "@framework/api/honeypot/engagement/utils";
import {
  toCycloneCountToken,
  type CommentContentSource,
} from "@framework/comments/comment-count-engagement-token";
import type { CommentCountNodeArgs } from "@framework/comments/comments.types";

/**
 * Identifies a Cyclone comment thread for both the live stream API and the
 * shared comment count store. Count keys use WP:/CY:/I5: prefixes via
 * {@link toCycloneCountId}; the stream API uses the unprefixed {@link streamId}.
 */
export interface CycloneCommentIdentity {
  /** Raw id for Cyclone's comment stream API (friendlyID). */
  streamId: string;
  /** Content system that owns this thread's id (drives WP:/CY:/I5: count keys). */
  contentSource: CommentContentSource;
}

/** Strips a WP:/CY:/I5: prefix when present; otherwise returns the trimmed id. */
export function normalizeCycloneStreamId(streamId: string): string {
  const trimmed = streamId.trim();
  if (!trimmed) {
    return "";
  }

  if (trimmed.startsWith("I5:")) {
    return trimmed.slice(3);
  }

  const parsed = toEngagementIdentity(trimmed);
  return parsed?.sourceId ?? trimmed;
}

/** Cyclone comment stream API friendlyID. */
export function toCycloneStreamId(identity: CycloneCommentIdentity): string {
  return normalizeCycloneStreamId(identity.streamId);
}

/** Comment count store / Honeypot key (WP:, CY:, or I5: prefixed). */
export function toCycloneCountId(identity: CycloneCommentIdentity): string {
  const streamId = normalizeCycloneStreamId(identity.streamId);
  if (!streamId) {
    return "";
  }

  return toCycloneCountToken(identity.contentSource, streamId);
}

/** Cyclone comment stream friendlyID from WordPress GraphQL node fields. */
export function getCycloneStreamIdFromNode(args: CommentCountNodeArgs): string {
  if (args.nodeId) {
    return args.nodeId;
  }

  return args.globalId || args.chorusUuid || "";
}
